import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import backendURL from '../../../config';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { Box, Typography, Paper, CircularProgress, Alert, TextField, Button, Container, Divider } from '@mui/material';
import PaymentForm from './PaymentForm';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const PublicPaymentPage = () => {
    const { payToken } = useParams();
    // Consolidate state into a single paymentData object
    const [paymentData, setPaymentData] = useState(null);
    const [clientSecret, setClientSecret] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [payerDetails, setPayerDetails] = useState({ customer_name: '', customer_email: '' });

    useEffect(() => {
        axios.get(`${backendURL}/public/payment-info/${payToken}`)
            .then(res => {
                setPaymentData(res.data.data);
            })
            .catch((err) => setError(err.response?.data?.message || 'This payment link is invalid or has expired.'))
            .finally(() => setIsLoading(false));
    }, [payToken]);

    const handlePreparePayment = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        try {
            const res = await axios.post(`${backendURL}/create-payment-intent`, {
                customer_name: payerDetails.customer_name,
                customer_email: payerDetails.customer_email,
                payment_token: paymentData.token,
            });
            setClientSecret(res.data.clientSecret);
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred.');
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) return <Container sx={{ textAlign: 'center', mt: 10 }}><CircularProgress /></Container>;
    if (error && !clientSecret) return <Container sx={{ mt: 10 }}><Alert severity="error">{error}</Alert></Container>;

    return (
        <Container component="main" maxWidth="sm" sx={{ mb: 4 }}>
            <Paper variant="outlined" sx={{ my: { xs: 3, md: 6 }, p: { xs: 2, md: 3 } }}>
                <Typography variant="h4" align="center" gutterBottom>
                    Payment to {paymentData?.display_name}
                </Typography>
                
                {!clientSecret ? (
                    <>
                        <Box sx={{ p: 2, my: 2, border: '1px solid #eee', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
                            <Typography variant="h2">{paymentData.title}</Typography>
                            <Typography variant="h5">{paymentData.payment_type}</Typography>
                            <Typography variant="h4" color="primary" sx={{ my: 1 }}>${parseFloat(paymentData.amount).toFixed(2)}</Typography>
                            {paymentData.description && <Typography variant="body2" color="text.secondary">{paymentData.description}</Typography>}
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="h6" gutterBottom>Your Details</Typography>
                        <Box component="form" onSubmit={handlePreparePayment}>
                            <TextField fullWidth required label="Your Name" margin="normal" onChange={e => setPayerDetails({...payerDetails, customer_name: e.target.value})} />
                            <TextField fullWidth required label="Your Email" type="email" margin="normal" onChange={e => setPayerDetails({...payerDetails, customer_email: e.target.value})} />
                            <Button type="submit" variant="contained" fullWidth sx={{ mt: 2 }} disabled={isLoading}>
                                {isLoading ? <CircularProgress size={24} /> : 'Proceed to Payment'}
                            </Button>
                        </Box>
                    </>
                ) : (
                    <Elements stripe={stripePromise} options={{ clientSecret }}>
                        <PaymentForm />
                    </Elements>
                )}
            </Paper>
        </Container>
    );
};

export default PublicPaymentPage;